import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { loadScenarios } from '@/data/scenarioLoader';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Clock, Plane, HelpCircle, Target, Users, DollarSign, MessageSquare, CloudLightning, AlertTriangle, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';

export const ScenarioSelector = ({ onSelectScenario }) => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Load scenarios metadata on component mount
    const loadedScenarios = loadScenarios();
    setScenarios(loadedScenarios);
  }, []);

  const handleSelect = (scenario) => {
    setSelectedScenario(scenario);
  };

  const handleStart = () => {
    if (selectedScenario) {
      onSelectScenario(selectedScenario);
    }
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty === 1) return 'bg-[hsl(var(--normal))]/20 text-[hsl(var(--normal))]';
    if (difficulty === 2) return 'bg-[hsl(var(--high))]/20 text-[hsl(var(--high))]';
    return 'bg-[hsl(var(--critical))]/20 text-[hsl(var(--critical))]';
  };

  const getDifficultyLabel = (difficulty) => {
    if (difficulty === 1) return 'Beginner';
    if (difficulty === 2) return 'Intermediate';
    return 'Advanced';
  };

  const getScenarioIcon = (crisisType) => {
    switch (crisisType) {
      case 'flight_cancelled':
        return XCircle;
      case 'weather_storm':
        return CloudLightning;
      case 'strike':
        return AlertTriangle;
      default:
        return Plane;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-6xl"
      >
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold mb-4 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--info))] bg-clip-text text-transparent"
          >
            Crisis Command
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground"
          >
            Aviation Crisis Training Simulator
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {scenarios.map((scenario, index) => (
            <motion.div
              key={scenario.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 + 0.3 }}
            >
              <Card
                className={`cursor-pointer transition-all duration-300 hover:scale-105 ${
                  selectedScenario?.id === scenario.id
                    ? 'ring-2 ring-[hsl(var(--primary))] shadow-lg'
                    : 'hover:shadow-md'
                }`}
                onClick={() => handleSelect(scenario)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={getDifficultyColor(scenario.difficulty)}>
                      {getDifficultyLabel(scenario.difficulty)}
                    </Badge>
                    <Badge variant="outline" className="font-mono">
                      {scenario.time.real_duration_minutes}min
                    </Badge>
                  </div>
                  <CardTitle className="text-xl mb-3">{scenario.name}</CardTitle>
                  
                  {/* Scenario Image */}
                  {scenario.image && (
                    <div className="mt-3 rounded-lg overflow-hidden border border-border">
                      <img 
                        src={scenario.image} 
                        alt={scenario.name}
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  )}
                </CardHeader>

                <CardContent>
                  <div className="flex gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-[hsl(var(--critical))]" />
                        <span className="text-muted-foreground">Severity:</span>
                        <span className="font-semibold">{scenario.crisis.severity}/5</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Plane className="w-4 h-4 text-[hsl(var(--info))]" />
                        <span className="text-muted-foreground">Type:</span>
                        <span className="font-semibold capitalize">
                          {scenario.crisis.type.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[hsl(var(--high))]" />
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="font-semibold">{Math.floor(scenario.time.duration_minutes / 60)}h</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[hsl(var(--info))]" />
                        <span className="text-muted-foreground">Start:</span>
                        <span className="font-semibold font-mono">{scenario.time.start}</span>
                      </div>
                    </div>
                    
                    {/* Scenario Icon */}
                    <div className="flex items-center justify-center">
                      {(() => {
                        const Icon = getScenarioIcon(scenario.crisis.type);
                        return <Icon className="w-16 h-16 text-muted-foreground/30" strokeWidth={1.5} />;
                      })()}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Crisis Details:</strong>
                    </p>
                    <p className="text-sm whitespace-pre-line">{scenario.crisis.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center space-y-4"
        >
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              variant="outline"
              onClick={() => setShowInstructions(true)}
              className="px-6 py-6 text-lg"
            >
              <HelpCircle className="w-5 h-5 mr-2" />
              Instructions
            </Button>
            <Button
              size="lg"
              onClick={handleStart}
              disabled={!selectedScenario}
              className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white px-8 py-6 text-lg font-semibold"
            >
              Start Training
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Instructions Modal */}
        <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">How to Use</DialogTitle>
              <DialogDescription>
                Crisis Command Training Guide
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Objective */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[hsl(var(--primary))]" />
                  <h3 className="text-lg font-semibold">Objective</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  You are a crisis center manager for an airline. Your task is to handle all affected passengers, 
                  offering them optimal solutions while maintaining high customer satisfaction (CSAT) and staying within budget.
                </p>
              </div>

              {/* How It Works */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[hsl(var(--info))]" />
                  <h3 className="text-lg font-semibold">How It Works</h3>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>1. Select a scenario</strong> — each scenario has its own difficulty and crisis type.</p>
                  <p><strong>2. Watch for customer arrivals</strong> — they appear in waves at specific times.</p>
                  <p><strong>3. Click on a customer card</strong> — this opens the decision panel.</p>
                  <p><strong>4. Choose solutions</strong> — transport, communication, accommodation, and compensation.</p>
                  <p><strong>5. Confirm the solution</strong> — press "Confirm Solution" button or Enter.</p>
                </div>
              </div>

              {/* Decision Making */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[hsl(var(--high))]" />
                  <h3 className="text-lg font-semibold">Decision Making</h3>
                </div>
                <div className="space-y-2">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-semibold mb-1">Transport (required)</p>
                    <p className="text-xs text-muted-foreground">
                      Choose an alternative route: plane, train, or combination. Consider time, cost, and comfort level.
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-semibold mb-1">Communication (required)</p>
                    <p className="text-xs text-muted-foreground">
                      Select channel (call, SMS, email, push) and communication tone (empathetic, business, minimal). 
                      Different customers value different approaches.
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-semibold mb-1">Hotel (optional)</p>
                    <p className="text-xs text-muted-foreground">
                      If overnight stay is needed — choose a hotel. "No Hotel" option means accommodation is not required.
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-semibold mb-1">Compensation (optional)</p>
                    <p className="text-xs text-muted-foreground">
                      Offer compensation: voucher, refund, miles, or just an apology. Consider budget and customer expectations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[hsl(var(--normal))]" />
                  <h3 className="text-lg font-semibold">Success Metrics</h3>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>CSAT (Customer Satisfaction)</strong> — average customer satisfaction level. Higher is better.</p>
                  <p><strong>Budget</strong> — remaining budget. Don't spend all money on first customers!</p>
                  <p><strong>Response Time</strong> — speed of response. The faster you react, the better.</p>
                </div>
              </div>

              {/* Tips */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[hsl(var(--critical))]" />
                  <h3 className="text-lg font-semibold">Tips</h3>
                </div>
                <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                  <li>Pay attention to customer tier: VIP requires special approach</li>
                  <li>Monitor time to departure — red bar means urgency</li>
                  <li>Read option descriptions — they contain important information</li>
                  <li>Balance between cost and customer satisfaction</li>
                  <li>Use pause (Space) to think through decisions</li>
                </ul>
              </div>

              {/* Controls */}
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <p className="text-sm font-semibold mb-2">Keyboard Shortcuts</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div><kbd className="px-2 py-1 bg-background rounded border">Space</kbd> — Pause/Resume</div>
                  <div><kbd className="px-2 py-1 bg-background rounded border">Enter</kbd> — Confirm Solution</div>
                  <div><kbd className="px-2 py-1 bg-background rounded border">Esc</kbd> — Cancel Customer Selection</div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
};